import React from 'react';
import { Box, Typography, Button, Container, Grid } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import LandingPreviews from '../components/LandingPreviews';
import BackgroundAnimation from '../components/BackgroundAnimation';
import FeatureHighlight from '../components/FeatureHighlight';
import TrackedButton from '../components/TrackedButton';

const Landing = () => {
  const navigate = useNavigate();

  return (
    <>
      <Box sx={{ 
        minHeight: 'calc(100vh - 64px)',
        marginTop: 0,
        background: 'linear-gradient(180deg, #0F172A 0%, #1E293B 100%)',
        color: 'white',
        overflow: 'hidden',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '100%',
          background: 'radial-gradient(circle at 50% 50%, rgba(59, 130, 246, 0.1) 0%, transparent 50%)',
          pointerEvents: 'none',
        }
      }}>
        <BackgroundAnimation />
        
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={6} alignItems="center" sx={{ minHeight: 'calc(100vh - 64px)', py: 8 }}>
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { xs: '2.5rem', md: '4rem' },
                    fontWeight: 800,
                    marginBottom: '1.5rem',
                    background: 'linear-gradient(90deg, #FFFFFF 0%, #94A3B8 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  Monetize Your AI Platform
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    color: '#94A3B8',
                    marginBottom: '3rem',
                  }}
                >
                  Seamlessly integrate ads into your LLM responses and maximize your revenue potential
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TrackedButton
                    variant="contained"
                    size="large"
                    onClick={() => navigate('/signup')}
                    sx={{
                      background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
                      padding: '1rem 2.5rem',
                      borderRadius: '12px',
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      boxShadow: '0 10px 20px rgba(37, 99, 235, 0.2)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
                      },
                    }}
                    trackingName="landing_cta"
                    trackingCategory="user_acquisition"
                    trackingData={{ location: "hero_section" }}
                  >
                    Get Started
                  </TrackedButton>
                  <TrackedButton
                    variant="outlined"
                    size="large"
                    onClick={() => navigate('/playground')}
                    sx={{
                      padding: '1rem 2.5rem',
                      borderRadius: '12px',
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      borderColor: 'rgba(255, 255, 255, 0.2)',
                      color: 'white',
                      '&:hover': {
                        borderColor: 'white',
                        background: 'rgba(255, 255, 255, 0.1)',
                      },
                    }}
                    trackingName="learn_more"
                    trackingCategory="user_engagement"
                    trackingData={{ location: "hero_section" }}
                  >
                    Learn More
                  </TrackedButton>
                </Box>
              </motion.div>
            </Grid>
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <Box
                  sx={{
                    width: '100%',
                    height: { xs: '400px', md: '500px' },
                    borderRadius: '24px',
                    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
                    background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.9) 100%)',
                    overflow: 'hidden',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                  }}
                >
                  <LandingPreviews key="landing-preview" />
                </Box>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>
      
      <FeatureHighlight />
    </>
  );
};

export default Landing; 